// Serviço para exportar e importar decks/subdecks
// Importação aceita .json ou .zip (um .json na raiz + imagens em qualquer subpasta,
// referenciadas por caminho relativo em media.images[].url) — ver importImages.ts

import type JSZip from 'jszip';
import {
    ImportImageError,
    MAX_IMAGE_BYTES,
    collectImageRefs,
    imageMimeFromPath,
    normalizeImagePath,
    rewriteImageUrls
} from './importImages';

// Recebe mensagens de progresso da importação (ex.: "Enviando imagens 12/30")
export type ImportProgress = (message: string) => void;

const UPLOAD_CONCURRENCY = 3;

interface ExportedCard {
    front: string;
    back: string;
    supportContent?: string;
    references?: string; // presente só se for override individual
    media?: {
        images?: Array<{ url: string; side: string }>;
        audios?: Array<{ url: string; side: string }>;
    };
}

interface ExportedApostilaContent {
    title?: string;
    pages?: any[];
    blocks?: any[];
    quizzes?: Record<string, any>;
}

interface ExportedSubdeck {
    name: string;
    description: string;
    references?: string; // subdeck-level (v1.1+)
    type?: 'flashcards' | 'apostila'; // ausente em exports anteriores a essa versão = flashcards
    cards: ExportedCard[]; // vazio para subdecks do tipo apostila
    // Conteúdo real de uma apostila vive num documento Resumo à parte — só é embutido
    // aqui se já havia sido salvo no momento da exportação (ver exportDeck)
    apostila?: ExportedApostilaContent;
}

interface ExportedDeck {
    name: string;
    description: string;
    subdecks: ExportedSubdeck[];
    exportedAt: string;
    version: string;
}

interface ExportedSubdeckFile {
    name: string;
    description: string;
    references?: string; // subdeck-level (v1.1+)
    cards: ExportedCard[];
    exportedAt: string;
    version: string;
}

class ExportImportService {

    // ========== EXPORTAR DECK ==========
    async exportDeck(deckId: string): Promise<void> {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`/api/decks/${deckId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (!response.ok) {
                throw new Error('Erro ao buscar deck');
            }

            const deck = await response.json();

            const subdecks: ExportedSubdeck[] = await Promise.all(
                (deck.subdecks || []).map(async (subdeck: any) => {
                    const subdeckRefs: string = subdeck.references || '';
                    const type: 'flashcards' | 'apostila' = subdeck.type === 'apostila' ? 'apostila' : 'flashcards';

                    const exported: ExportedSubdeck = {
                        name: subdeck.name,
                        description: subdeck.description || '',
                        references: subdeckRefs,
                        type,
                        cards: (subdeck.cards || []).map((card: any) => {
                            const cardData: ExportedCard = {
                                front: card.front,
                                back: card.back,
                                supportContent: card.supportContent || ''
                            };
                            // Incluir references no card apenas se for override individual
                            const cardRefs = card.references || '';
                            if (cardRefs && cardRefs !== subdeckRefs) {
                                cardData.references = cardRefs;
                            }
                            // Incluir media apenas se houver imagens ou áudios
                            if (card.media && (card.media.images?.length || card.media.audios?.length)) {
                                cardData.media = {
                                    images: card.media.images || [],
                                    audios: card.media.audios || []
                                };
                            }
                            return cardData;
                        })
                    };

                    // Apostila: o conteúdo real (pages/blocks/quizzes) vive num Resumo à parte —
                    // embute aqui se já houver algo salvo, pra reimportação vir completa
                    if (type === 'apostila') {
                        try {
                            const resumoResponse = await fetch(
                                `/api/decks/${deckId}/subdecks/${subdeck._id}/resumo`,
                                { headers: { Authorization: `Bearer ${token}` } }
                            );
                            if (resumoResponse.ok) {
                                const { resumo } = await resumoResponse.json();
                                exported.apostila = {
                                    title: resumo.title,
                                    pages: resumo.pages || [],
                                    blocks: resumo.blocks || [],
                                    quizzes: resumo.quizzes || {}
                                };
                            }
                            // 404 = apostila ainda sem conteúdo salvo — exporta só os metadados do subdeck
                        } catch {
                            // Falha ao buscar o resumo de UMA apostila não deve abortar a exportação do deck inteiro
                        }
                    }

                    return exported;
                })
            );

            const exportData: ExportedDeck = {
                name: deck.name,
                description: deck.description || '',
                subdecks,
                exportedAt: new Date().toISOString(),
                version: '1.1'
            };

            this.downloadJSON(exportData, `deck-${deck.name}-${Date.now()}.json`);

            console.log('✅ Deck exportado:', exportData);
        } catch (error) {
            console.error('❌ Erro ao exportar deck:', error);
            throw error;
        }
    }

    // ========== EXPORTAR CUSTOM DECK ==========
    async exportCustomDeck(deckId: string): Promise<void> {
        try {
            const token = localStorage.getItem('accessToken');

            const response = await fetch(`/api/custom-decks/${deckId}/export`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (!response.ok) {
                throw new Error('Erro ao buscar deck personalizado');
            }

            const exportData = await response.json();

            const finalExportData = {
                ...exportData,
                exportedAt: new Date().toISOString(),
                version: '1.1'
            };

            this.downloadJSON(finalExportData, `custom-deck-${exportData.name}-${Date.now()}.json`);

            console.log('✅ Custom deck exportado:', finalExportData);
        } catch (error) {
            console.error('❌ Erro ao exportar custom deck:', error);
            throw error;
        }
    }

    // ========== EXPORTAR SUBDECK ==========
    async exportSubdeck(deckId: string, subdeckId: string, isCustomDeck: boolean = false): Promise<void> {
        try {
            const token = localStorage.getItem('accessToken');

            const baseUrl = isCustomDeck
                ? `/api/custom-decks/${deckId}/subdecks/${subdeckId}`
                : `/api/decks/${deckId}/subdecks/${subdeckId}`;

            console.log('📤 [EXPORT] Exportando de:', baseUrl);

            const response = await fetch(baseUrl, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (!response.ok) {
                throw new Error('Erro ao buscar subdeck');
            }

            const data = await response.json();
            const subdeck = data.subdeck;
            const subdeckRefs: string = subdeck.references || '';

            const exportData: ExportedSubdeckFile = {
                name: subdeck.name,
                description: subdeck.description || '',
                references: subdeckRefs,
                cards: subdeck.cards.map((card: any) => {
                    const cardData: ExportedCard = {
                        front: card.front,
                        back: card.back,
                        supportContent: card.supportContent || ''
                    };
                    // Incluir references no card apenas se for override individual
                    const cardRefs = card.references || '';
                    if (cardRefs && cardRefs !== subdeckRefs) {
                        cardData.references = cardRefs;
                    }
                    // Incluir media apenas se houver imagens ou áudios
                    if (card.media && (card.media.images?.length || card.media.audios?.length)) {
                        cardData.media = {
                            images: card.media.images || [],
                            audios: card.media.audios || []
                        };
                    }
                    return cardData;
                }),
                exportedAt: new Date().toISOString(),
                version: '1.1'
            };

            this.downloadJSON(exportData, `subdeck-${subdeck.name}-${Date.now()}.json`);

            console.log('✅ Subdeck exportado:', exportData);
        } catch (error) {
            console.error('❌ Erro ao exportar subdeck:', error);
            throw error;
        }
    }

    // ========== CRIAR DECK + SUBDECKS A PARTIR DE UM ExportedDeck (uso interno) ==========
    // Compartilhado por importDeck (GRAIA, vincula a um Cycle) e importDeckAsRyanSpace (RYAN, vincula a um RyanSpace)
    private async createDeckWithSubdecks(data: ExportedDeck): Promise<{ deckId: string; apostilasWithoutContent: number }> {
        const token = localStorage.getItem('accessToken');

        const deckResponse = await fetch('/api/decks', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({
                name: data.name,
                description: data.description
            })
        });

        if (!deckResponse.ok) {
            throw new Error('Erro ao criar deck');
        }

        const newDeck = await deckResponse.json();
        const deckId = newDeck._id;
        let apostilasWithoutContent = 0;

        for (const subdeck of data.subdecks) {
            // Referência no nível do subdeck (v1.1) — compatível com v1.0 (undefined)
            const subdeckRefs = subdeck.references || '';
            // Ausência de `type` no export = arquivo antigo, anterior ao suporte a apostila = flashcards
            const type: 'flashcards' | 'apostila' = subdeck.type === 'apostila' ? 'apostila' : 'flashcards';

            const subdeckResponse = await fetch(
                `/api/decks/${deckId}/subdecks`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        name: subdeck.name,
                        description: subdeck.description,
                        type
                    })
                }
            );

            if (!subdeckResponse.ok) {
                continue;
            }

            // POST /api/decks/:deckId/subdecks responde { success, subdeck: {...} } —
            // um único objeto, não { subdecks: [...] } (esse formato é só da rota PUT de update)
            const created = await subdeckResponse.json();
            const subdeckId = created.subdeck?._id;
            if (!subdeckId) continue;

            if (type === 'apostila') {
                // Conteúdo de apostila vive num Resumo à parte — só existe se o export original já o tinha embutido
                const hasEmbeddedContent = !!subdeck.apostila
                    && ((subdeck.apostila.pages && subdeck.apostila.pages.length > 0)
                        || (subdeck.apostila.blocks && subdeck.apostila.blocks.length > 0));

                if (hasEmbeddedContent) {
                    await fetch(
                        `/api/decks/${deckId}/subdecks/${subdeckId}/resumo`,
                        {
                            method: 'PUT',
                            headers: {
                                'Content-Type': 'application/json',
                                Authorization: `Bearer ${token}`
                            },
                            body: JSON.stringify({
                                title: subdeck.apostila!.title || subdeck.name,
                                blocks: subdeck.apostila!.blocks || [],
                                pages: subdeck.apostila!.pages || [],
                                quizzes: subdeck.apostila!.quizzes || {},
                                isPublished: true
                            })
                        }
                    );
                } else {
                    apostilasWithoutContent++;
                }
                continue;
            }

            // Flashcards: só faz o PUT se realmente houver cards (guarda defensiva contra
            // arquivos malformados/antigos onde `cards` poderia não ser um array)
            if (Array.isArray(subdeck.cards) && subdeck.cards.length > 0) {
                await fetch(
                    `/api/decks/${deckId}/subdecks/${subdeckId}`,
                    {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`
                        },
                        body: JSON.stringify({
                            name: subdeck.name,
                            description: subdeck.description,
                            references: subdeckRefs,
                            cards: subdeck.cards.map(card => ({
                                front: card.front,
                                back: card.back,
                                supportContent: card.supportContent || '',
                                references: card.references || '',
                                media: card.media || { images: [], audios: [] }
                            }))
                        })
                    }
                );
            }
        }

        return { deckId, apostilasWithoutContent };
    }

    // ========== IMPORTAR DECK ==========
    async importDeck(file: File, cycleId?: string, onProgress?: ImportProgress): Promise<{ apostilasWithoutContent: number }> {
        try {
            const { data: content, zip } = await this.readImportFile(file);
            let data = content as ExportedDeck;

            if (!data.name || !data.subdecks) {
                throw new Error('Arquivo inválido: falta nome ou subdecks');
            }

            data = await this.resolveImportImages(data, zip, onProgress);

            const { deckId, apostilasWithoutContent } = await this.createDeckWithSubdecks(data);

            if (cycleId) {
                const token = localStorage.getItem('accessToken');
                await fetch(`/api/cycles/${cycleId}/decks/${deckId}`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}` }
                });
            }

            console.log('✅ Deck importado com sucesso');
            return { apostilasWithoutContent };
        } catch (error) {
            console.error('❌ Erro ao importar deck:', error);
            throw error;
        }
    }

    // ========== IMPORTAR DECK COMO CAMPO DE ESTUDO (RYAN) ==========
    // Reaproveita a mesma criação de Deck/Subdecks do importDeck, mas em vez de vincular
    // a um Cycle (GRAIA), cria um RyanSpace novo apontando pro deck recém-criado.
    async importDeckAsRyanSpace(file: File, meta: {
        name?: string;
        description?: string;
        icon?: string;
        colorId?: string;
        theme?: string;
        groupId?: string | null;
        isFree?: boolean;
    } = {}, onProgress?: ImportProgress): Promise<any> {
        try {
            const { data: content, zip } = await this.readImportFile(file);
            let data = content as ExportedDeck;

            if (!data.name || !data.subdecks) {
                throw new Error('Arquivo inválido: falta nome ou subdecks');
            }

            data = await this.resolveImportImages(data, zip, onProgress);

            const { deckId, apostilasWithoutContent } = await this.createDeckWithSubdecks(data);

            const token = localStorage.getItem('accessToken');
            const spaceResponse = await fetch('/api/ryan/admin/spaces', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    name: meta.name || data.name,
                    description: meta.description ?? data.description ?? '',
                    icon: meta.icon || 'BookOpen',
                    colorId: meta.colorId || 'floresta',
                    theme: meta.theme || 'default',
                    groupId: meta.groupId ?? null,
                    isFree: meta.isFree || false,
                    deckId
                })
            });

            if (!spaceResponse.ok) {
                const errData = await spaceResponse.json().catch(() => ({} as any));
                throw new Error(errData.message || 'Erro ao criar campo de estudo');
            }

            console.log('✅ Campo de estudo importado com sucesso');
            const result = await spaceResponse.json();
            return { ...result, apostilasWithoutContent };
        } catch (error) {
            console.error('❌ Erro ao importar campo de estudo:', error);
            throw error;
        }
    }

    /**
     * Importa um deck como CUSTOM DECK
     */
    async importCustomDeck(file: File, cycleId?: string): Promise<void> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = async (e) => {
                try {
                    const content = e.target?.result as string;
                    const deckData = JSON.parse(content);

                    if (!deckData.name || !deckData.subdecks) {
                        throw new Error('Arquivo JSON inválido: faltam campos obrigatórios');
                    }

                    const token = localStorage.getItem('accessToken');
                    if (!token) {
                        throw new Error('Usuário não autenticado');
                    }

                    const response = await fetch('/api/custom-decks/import', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`
                        },
                        body: JSON.stringify({
                            deckData,
                            cycleId
                        })
                    });

                    if (!response.ok) {
                        const errorData = await response.json();
                        throw new Error(errorData.message || 'Erro ao importar deck personalizado');
                    }

                    resolve();
                } catch (error: any) {
                    reject(new Error(`Erro ao processar arquivo: ${error.message}`));
                }
            };

            reader.onerror = () => {
                reject(new Error('Erro ao ler arquivo'));
            };

            reader.readAsText(file);
        });
    }

    // ========== IMPORTAR SUBDECK ==========
    async importSubdeck(file: File, deckId: string, isCustomDeck: boolean = false, groupId?: string, onProgress?: ImportProgress): Promise<void> {
        try {
            const { data: content, zip } = await this.readImportFile(file);
            let data = content as ExportedSubdeckFile;

            if (!data.name || !data.cards) {
                throw new Error('Arquivo inválido: falta nome ou cards');
            }

            data = await this.resolveImportImages(data, zip, onProgress);

            const token = localStorage.getItem('accessToken');

            // Referência no nível do subdeck (v1.1) — vazio em v1.0
            const subdeckRefs = data.references || '';

            const createUrl = isCustomDeck
                ? `/api/custom-decks/${deckId}/subdecks`
                : `/api/decks/${deckId}/subdecks`;

            console.log('📥 [IMPORT] Criando subdeck em:', createUrl);

            const subdeckResponse = await fetch(createUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    name: data.name,
                    description: data.description,
                    ...(groupId ? { groupId } : {})
                })
            });

            if (!subdeckResponse.ok) {
                throw new Error('Erro ao criar subdeck');
            }

            const subdeckData = await subdeckResponse.json();

            console.log('📦 [IMPORT] Resposta do backend:', subdeckData);

            let newSubdeckId;

            if (subdeckData.subdecks && subdeckData.subdecks.length > 0) {
                const lastSubdeck = subdeckData.subdecks[subdeckData.subdecks.length - 1];
                newSubdeckId = lastSubdeck._id;
                console.log('✅ [IMPORT] Subdeck encontrado no array:', newSubdeckId);
            } else if (subdeckData.subdeck && subdeckData.subdeck._id) {
                newSubdeckId = subdeckData.subdeck._id;
                console.log('✅ [IMPORT] Subdeck encontrado em subdeck._id:', newSubdeckId);
            } else if (subdeckData._id && !subdeckData.subdecks) {
                newSubdeckId = subdeckData._id;
                console.log('✅ [IMPORT] Subdeck encontrado direto:', newSubdeckId);
            } else {
                console.error('❌ [IMPORT] Estrutura inválida:', subdeckData);
                throw new Error('Não foi possível encontrar o ID do subdeck criado');
            }

            console.log('✅ [IMPORT] Subdeck criado com ID:', newSubdeckId);

            const updateUrl = isCustomDeck
                ? `/api/custom-decks/${deckId}/subdecks/${newSubdeckId}`
                : `/api/decks/${deckId}/subdecks/${newSubdeckId}`;

            console.log('📥 [IMPORT] Adicionando cards em:', updateUrl);

            const cardsResponse = await fetch(updateUrl, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    name: data.name,
                    description: data.description,
                    references: subdeckRefs,
                    cards: data.cards.map(card => ({
                        front: card.front,
                        back: card.back,
                        supportContent: card.supportContent || '',
                        references: card.references || '',
                        media: card.media || { images: [], audios: [] }
                    }))
                })
            });

            if (!cardsResponse.ok) {
                throw new Error('Erro ao adicionar cards');
            }

            console.log('✅ Subdeck importado com sucesso');
        } catch (error) {
            console.error('❌ Erro ao importar subdeck:', error);
            throw error;
        }
    }

    // ========== LEITURA DO ARQUIVO DE IMPORTAÇÃO (.json ou .zip) ==========

    // Só lê e devolve o JSON do deck (sem validar/enviar imagens) — usado para
    // pré-preencher formulários antes da importação de fato
    async readDeckFile(file: File): Promise<any> {
        const { data } = await this.readImportFile(file);
        return data;
    }

    private isZipFile(file: File): boolean {
        return /\.zip$/i.test(file.name) || /zip/i.test(file.type);
    }

    // Público para fluxos que criam o subdeck por rota própria (ex.: RyanFieldPage)
    // e só precisam da leitura + resolução de imagens: readImportFile → validar → resolveImportImages
    async readImportFile(file: File): Promise<{ data: any; zip: JSZip | null }> {
        if (!this.isZipFile(file)) {
            return { data: await this.readJSONFile(file), zip: null };
        }

        // Import dinâmico: o JSZip só é baixado quando alguém importa um .zip
        const { default: JSZipLib } = await import('jszip');

        let zip: JSZip;
        try {
            zip = await JSZipLib.loadAsync(file);
        } catch {
            throw new Error('Arquivo .zip inválido ou corrompido');
        }

        const rootJsons = Object.values(zip.files).filter(entry => {
            const name = normalizeImagePath(entry.name);
            return !entry.dir && !name.includes('/') && /\.json$/i.test(name);
        });

        if (rootJsons.length !== 1) {
            throw new Error(
                rootJsons.length === 0
                    ? 'O .zip precisa ter um arquivo .json na raiz (fora de pastas)'
                    : `O .zip deve ter apenas um arquivo .json na raiz — encontrados ${rootJsons.length}: ${rootJsons.map(e => e.name).join(', ')}`
            );
        }

        const text = (await rootJsons[0].async('string')).replace(/^﻿/, '');
        try {
            return { data: JSON.parse(text), zip };
        } catch {
            throw new Error(`Arquivo JSON inválido dentro do .zip: ${rootJsons[0].name}`);
        }
    }

    // ========== IMAGENS: VALIDAR, ENVIAR AO CLOUDINARY E REESCREVER URLs ==========
    // Roda antes de qualquer criação no banco: qualquer problema aborta a importação inteira.
    async resolveImportImages<T>(data: T, zip: JSZip | null, onProgress?: ImportProgress): Promise<T> {
        const refs = collectImageRefs(data); // lança se houver base64
        if (refs.length === 0) return data;

        if (!zip) {
            throw new ImportImageError(
                `O arquivo tem ${refs.length} imagem(ns) com caminho relativo (ex.: ${refs[0].path}). ` +
                'Importe o .zip contendo o JSON e as imagens.'
            );
        }

        const entries = new Map<string, JSZip.JSZipObject>();
        zip.forEach((_, entry) => {
            if (!entry.dir) entries.set(normalizeImagePath(entry.name), entry);
        });

        const missing = refs.filter(ref => !entries.has(ref.path)).map(ref => ref.path);
        if (missing.length > 0) {
            throw new ImportImageError(
                `${missing.length} imagem(ns) referenciada(s) no JSON não existe(m) no .zip: ${missing.join(', ')}`
            );
        }

        // Mesmos limites da rota de upload — valida tudo antes do primeiro envio
        const invalid: string[] = [];
        for (const ref of refs) {
            if (!imageMimeFromPath(ref.path)) {
                invalid.push(`${ref.path} (formato não aceito)`);
                continue;
            }
            const blob = await entries.get(ref.path)!.async('blob');
            if (blob.size > MAX_IMAGE_BYTES) {
                invalid.push(`${ref.path} (${(blob.size / 1024 / 1024).toFixed(1)} MB)`);
            }
        }
        if (invalid.length > 0) {
            throw new ImportImageError(
                `Imagem(ns) fora do limite da plataforma (PNG, JPG, SVG, WEBP ou GIF, até 10 MB): ${invalid.join(', ')}`
            );
        }

        // Upload: uma vez por caminho (refs já vem sem duplicatas), no máximo 3 simultâneos
        const urlMap: Record<string, string> = {};
        const state: { next: number; done: number; failure: { path: string; message: string } | null } =
            { next: 0, done: 0, failure: null };

        onProgress?.(`Enviando imagens 0/${refs.length}`);

        const worker = async () => {
            while (!state.failure && state.next < refs.length) {
                const ref = refs[state.next++];
                try {
                    const blob = await entries.get(ref.path)!.async('blob');
                    // Blob do JSZip vem sem MIME — a rota filtra por mimetype, então o tipo é definido pela extensão
                    const imageFile = new File([blob], ref.path.split('/').pop() || ref.path, {
                        type: imageMimeFromPath(ref.path)!
                    });
                    urlMap[ref.path] = await this.uploadCardImage(imageFile, ref.subdeckName);
                    state.done++;
                    onProgress?.(`Enviando imagens ${state.done}/${refs.length}`);
                } catch (error: any) {
                    if (!state.failure) state.failure = { path: ref.path, message: error?.message || 'erro desconhecido' };
                }
            }
        };

        await Promise.all(Array.from({ length: Math.min(UPLOAD_CONCURRENCY, refs.length) }, worker));

        if (state.failure) {
            console.error(
                '❌ [IMPORT] Upload de imagem falhou. Imagens já enviadas ao Cloudinary (para limpeza manual):',
                Object.values(urlMap)
            );
            throw new ImportImageError(
                `Falha ao enviar a imagem "${state.failure.path}": ${state.failure.message}. ` +
                'Importação cancelada — nada foi criado.'
            );
        }

        console.log(`✅ [IMPORT] ${refs.length} imagem(ns) enviada(s) ao Cloudinary`);
        onProgress?.('Salvando cards...');

        return rewriteImageUrls(data, urlMap);
    }

    private async uploadCardImage(file: File, subdeckName: string): Promise<string> {
        const formData = new FormData();
        formData.append('image', file);
        formData.append('subdeckName', subdeckName);

        const token = localStorage.getItem('accessToken');
        const response = await fetch('/api/upload/card-image-cloudinary', {
            method: 'POST',
            body: formData,
            headers: { Authorization: `Bearer ${token}` }
        });

        const body = await response.json().catch(() => ({} as any));
        if (!response.ok || !body.url) {
            throw new Error(body.message || `HTTP ${response.status}`);
        }
        return body.url;
    }

    // ========== HELPERS ==========

    private downloadJSON(data: any, filename: string): void {
        const json = JSON.stringify(data, null, 2);
        const blob = new Blob([json], { type: 'application/json' });
        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);
    }

    private readJSONFile(file: File): Promise<any> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = (e) => {
                try {
                    const content = JSON.parse(e.target?.result as string);
                    resolve(content);
                } catch (error) {
                    reject(new Error('Arquivo JSON inválido'));
                }
            };

            reader.onerror = () => reject(new Error('Erro ao ler arquivo'));
            reader.readAsText(file);
        });
    }
}

export const exportImportService = new ExportImportService();
